/**
 * `<html lang="nb">` guard (Plan 01-10 Task 4).
 *
 * Sets the document language to Norwegian Bokmål before React mounts,
 * then installs a MutationObserver that resets `lang` back to `'nb'`
 * (and logs a `console.warn`) if anything else mutates it later —
 * browser extensions that auto-translate, third-party scripts, a buggy
 * a11y tool. Drift is quiet-by-default in browsers; this guard makes
 * it loud enough to notice in devtools without breaking the page.
 *
 * SSR-safe: no-op when `document` is undefined.
 *
 * Returns a disconnect function so tests can clean up; production
 * callers (`main.tsx`) discard the return — the observer should live
 * for the lifetime of the document.
 */

export function applyLangGuard(): (() => void) | undefined {
  if (typeof document === 'undefined') return undefined;

  const html = document.documentElement;
  html.lang = 'nb';

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.attributeName === 'lang' && html.lang !== 'nb') {
        // Intentional dev-visible warning for locale drift.
        console.warn(
          'lang drift detected; resetting from',
          html.lang,
          'to nb',
        );
        html.lang = 'nb';
      }
    }
  });
  observer.observe(html, {
    attributes: true,
    attributeFilter: ['lang'],
  });

  return () => observer.disconnect();
}
